import { request, requestJSON } from '@codametrix/client-api';
import {
  appServiceLines,
  caseUtils,
  cmxDateTime,
  codeBenchActions,
  commonEnums,
  HttpMethods,
  navigationIntentActions
} from '@codametrix/ui-common';
import type {
  ActionPermissions,
  CodingScreenApi,
  ExcludePayload,
  HoldPayload,
  TransferPayload
} from '../../project_starburst';
import {
  REASON_DICTIONARY_NAME,
  type ReasonKind,
  type ReasonOption,
  TransferNotSentError
} from '../../project_starburst/interface';
import {
  canExcludeFromConfig,
  mergedJsonValue,
  transferPermissionFromConfig
} from './apiMappers/actionPermissions';
import { toProviderDirectoryEntry } from './apiMappers/providerDirectory';
import { parseReasonRows, REASON_COLUMNS } from './apiMappers/reasonDictionary';
import {
  toTransferCaseInput,
  toTransferError
} from './apiMappers/transferCase';

export type DataAccessContext = {
  readonly sourceId: string;
  readonly serviceLine: string;
  readonly healthSystemUid: string;
  readonly tenantUid: string;
};

import { gql } from '@apollo/client';
import type { getServiceDeskClient } from '../../clients/service-desk.client';
import { api as netApi } from '../../core/net';
import type { TransferRecipient } from '../../project_starburst';
import type {
  BillingLocationUpdate,
  CaseSpecialtyUpdate,
  DictionaryOption,
  DictionaryRequest,
  PatientTypeUpdate
} from '../../project_starburst/interface/api';
import {
  narrowByTerm,
  toTransferRecipients
} from './apiMappers/transferRecipients';

/**
 * The one test a dictionary cell must pass to be offered as an option or sent as a value.
 *
 * Module-scoped so the reader and the writer share it literally rather than by convention. The
 * backend stores `patient_class` as an override with no null check and layers it over the real
 * value at every later read, so there is no way to un-set a bad one from here -- which is why the
 * write boundary re-applies this rather than trusting its caller.
 */
const isUsableCell = (cell: unknown): cell is string =>
  typeof cell === 'string' && cell.length > 0;

/**
 * the coder directory behind the Transfer modal's type-ahead.
 *
 * A DASHBOARD-OWNED document deliberately: the equivalent query lives in `packages/service-desk`,
 * which dashboard-ui does not depend on and `project_starburst` may not import. Only the transport
 * (`getServiceDeskClient`) is shared, exactly as `actions/manage-coders.action.ts` does it.
 */
const GET_ALL_CODERS = gql`
  query Coders {
    Coders {
      data
      total
    }
  }
`;

type ServiceDeskClient = ReturnType<typeof getServiceDeskClient>;

const TRANSFER_CASE = gql`
  mutation TransferCase($input: TransferCaseInput!) {
    TransferCase(input: $input) { ok destination_name }
  }
`;

const TRANSFER_QUEUES = gql`
  query TransferQueues($service_line: String!, $exclude_collection_id: String) {
    TransferQueues(service_line: $service_line, exclude_collection_id: $exclude_collection_id) {
      collection_id name description group_id case_count
    }
  }
`;

const TRANSFER_RECIPIENTS = gql`
  query TransferRecipients { TransferRecipients { data total } }
`;

export async function fetchEditGateProcessInstance(
  caseRun: CMxAPI.CaseRun
): Promise<AmplifyAPI.EditGateProcessInstance | null> {
  const serviceLineConfig = appServiceLines.determineServiceLineConfig({
    name: caseRun.service_line
  });
  const processInstance = await netApi<
    AmplifyAPI.EditGateProcessInstance | undefined
  >({
    endpoint:
      `/coding-api/processinstance/${serviceLineConfig.amplifyPath}/` +
      `${caseRun.process_instance_guid}/v1`
  });
  if (!processInstance?.status) return null;
  const {
    status,
    cancelled,
    isChargeable,
    qaStatus,
    holdSubmission,
    holdSubmissionDate,
    isCurrentInvoiceSubmitted,
    caseType
  } = processInstance;
  return {
    status,
    cancelled,
    isChargeable,
    qaStatus,
    holdSubmission,
    holdSubmissionDate,
    isCurrentInvoiceSubmitted,
    caseType
  };
}

/**
 * Reconstruct the hold endpoint's `ProcessInstance{serviceLine, guid.uuid}` from the fetched coding
 * case. `state.codeBench.processInstance` is NOT reliably populated on the `/project_starburst/`
 * route (only amplify case-load actions write it), so read the authoritative values off the primary
 * `CaseRun` — the same `process_instance_guid` + resolved `amplifyPath` the app's own amplify fetch
 * uses (`getDetail` → `amplifyProcessInstance`). Returns undefined until the case has loaded, or if
 * the service line can't be resolved — and NEVER throws (a throw here would crash the shell render).
 */
export function processInstanceFromCase(
  activeCase: CMxAPI.Case | undefined
): AmplifyAPI.ProcessInstance | undefined {
  if (!activeCase?.caseRuns?.length) return undefined;
  try {
    const caseRun = caseUtils.getCaseRun(activeCase);
    const uuid = caseRun?.process_instance_guid;
    if (!uuid) return undefined;
    const slConfig = appServiceLines.determineServiceLineConfig({
      name: caseRun.service_line,
      caseIdentifier: String(caseRun.case_id ?? '')
    });
    if (!slConfig?.amplifyPath) return undefined;
    return {
      serviceLine: slConfig.amplifyPath,
      guid: { uuid }
    } as AmplifyAPI.ProcessInstance;
  } catch {
    return undefined;
  }
}

const IPC_LONG_NAME = 'Inpatient Professional Coding';

const amplifyServiceLineAlias = (serviceLine: string): string =>
  serviceLine === IPC_LONG_NAME ? 'ipc' : serviceLine;

/** The `dataaccess` branch of `navigateProvider` (`ui-common`'s `code-bench.actions.ts`). */
async function fetchProviderViaDataAccess(
  dataAccessContext: DataAccessContext,
  providerName: string | undefined,
  tenantCode: string | undefined
): Promise<XCaliberAPI.ProviderEntityIPC[] | undefined> {
  const body: CMxCommonApp.ProviderSearchRequest = {
    ...dataAccessContext,
    name: 'searchForProvidersByName',
    action: 'read',
    actionDestination: 'searchbillingprovider',
    sort: 'Asc',
    limit: 1000,
    filterFieldsMap: {
      provider_name: providerName,
      ...(tenantCode ? { location_name: tenantCode } : {})
    }
  };
  const result = await requestJSON<
    CMxAPI.PageableList<XCaliberAPI.ProviderEntityIPC>
  >('/dataaccess/read/v1', { method: 'POST', body: JSON.stringify(body) });
  return result?.content;
}

/**
 * Build the `CodingScreenApi` the coding shell injects. ONLY `placeHold` is wired to the real backend
 * (direct `PUT /coding-api/.../hold/v1` via client-api's `request`, which throws on non-2xx so a failed
 * hold actually surfaces); the rest are resolving stubs: `loadNextCase` needs the worklist cursor
 * context this route lacks, and the "Logged as" current-user has no redux source yet — both PT-1 on CDMX-18412.
 *
 * Returns undefined until the case + its ProcessInstance are available, during which `HoldAction`
 * shows "Holds are not available yet." (its own no-api guard).
 */
export function codingScreenApiFromCase(
  activeCase: CMxAPI.Case | undefined,
  dispatch: (action: unknown) => unknown,
  dataAccessContext: DataAccessContext,
  reloadCase: () => void,
  tenantId: string,
  currentProfile?: string | null
): CodingScreenApi | undefined {
  const processInstance = processInstanceFromCase(activeCase);
  if (!processInstance) return undefined;
  const caseServiceLine = caseUtils.getCaseRun(activeCase)?.service_line ?? '';
  return {
    placeHold: async (payload: HoldPayload): Promise<void> => {
      // Issue the hold PUT DIRECTLY via client-api's `request`, which THROWS on any non-2xx
      // (ServerError/BadRequestError/UnauthenticatedError/…) -> this promise rejects -> HoldAction's
      // placeHold `catch` shows the error banner. We deliberately do NOT go through ui-common's
      // `holdCase` thunk: it catches the request error, fires a legacy toast, and still resolves
      // `fulfilled`, so its `.unwrap()` never rejects and a FAILED hold would present as success.
      await request(
        `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/hold/v1`,
        {
          cache: 'no-cache',
          redirect: 'follow',
          referrer: 'no-referrer',
          method: 'PUT',
          body: JSON.stringify({
            reasons: payload.reasons,
            comment: payload.note?.trim() ?? '',
            case_context: payload.caseContext ?? null,
            hold_reason: 'HOLD',
            coding_manager_id: null
          })
        }
      );
      // Refresh the case's hold status into redux (the side effect the `holdCase` thunk used to
      // provide; PT-1 will consume it to reflect the now-on-hold state). Fire-and-forget: the hold
      // already persisted above, so a status-refresh failure must never fail the placeHold.
      dispatch(codeBenchActions.getHoldCaseForProcessInstance(processInstance));
    },
    excludeCase: async (payload: ExcludePayload): Promise<void> => {
      await request(
        `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/chargeable/v1?isChargeable=false`,
        {
          cache: 'no-cache',
          redirect: 'follow',
          referrer: 'no-referrer',
          method: 'PUT',
          body: JSON.stringify({
            reasons: payload.reasons,
            note: payload.note ?? null,
            case_context: payload.caseContext ?? null
          })
        }
      );
      dispatch(codeBenchActions.fetchActivity(processInstance));
    },
    updateBillingLocation: async ({
      caseRunUid,
      serviceLine,
      billingLocation
    }: BillingLocationUpdate): Promise<void> => {
      // `request` resolves for 405/410/422/428 instead of throwing, so a rejected
      // edit would otherwise read as a successful save.
      const response = await request(
        `/coding-api/processinstance/${amplifyServiceLineAlias(serviceLine)}/${caseRunUid}/v1`,
        {
          cache: 'no-cache',
          redirect: 'follow',
          referrer: 'no-referrer',
          method: 'PUT',
          body: JSON.stringify({ billing_location: billingLocation })
        }
      );
      if (!response.ok) {
        throw new Error(`Facility update failed with ${response.status}`);
      }

      // Past this line the change is committed; nothing below may reject, and
      // neither side effect may cost the other.
      try {
        const updated = await response.json();
        dispatch(codeBenchActions.fetchActivity(updated) as never);
      } catch (error) {
        console.error(
          'Facility saved, but the activity dispatch failed',
          error
        );
      }
      try {
        reloadCase();
      } catch (error) {
        console.error('Facility saved, but the refresh failed', error);
      }
    },
    updatePatientType: async ({
      caseRunUid,
      serviceLine,
      newValue,
      patientClass
    }: PatientTypeUpdate): Promise<void> => {
      // `request` resolves for 405/410/422/428 instead of throwing, so a rejected
      // edit would otherwise read as a successful save.
      const response = await request(
        `/coding-api/processinstance/${amplifyServiceLineAlias(serviceLine)}/${caseRunUid}/v1`,
        {
          cache: 'no-cache',
          redirect: 'follow',
          referrer: 'no-referrer',
          method: 'PUT',
          // `patient_class` rides along because nothing on this path derives it. Guarded by the
          // predicate and not by `!== undefined`, which looks equivalent and is not: `undefined`
          // needs no guard at all (`JSON.stringify` drops it), while `null` passes that check and
          // reaches the wire -- which is exactly what the legacy editor still does.
          body: JSON.stringify({
            patient_type: newValue,
            ...(isUsableCell(patientClass) && { patient_class: patientClass })
          })
        }
      );
      if (!response.ok) {
        throw new Error(`Patient type update failed with ${response.status}`);
      }

      // Past this line the change is committed; nothing below may reject, and
      // neither side effect may cost the other.
      try {
        const updated = await response.json();
        dispatch(codeBenchActions.fetchActivity(updated) as never);
      } catch (error) {
        console.error(
          'Patient type saved, but the activity dispatch failed',
          error
        );
      }
      try {
        reloadCase();
      } catch (error) {
        console.error('Patient type saved, but the refresh failed', error);
      }
    },
    updateCaseSpecialty: async ({
      caseRunUid,
      serviceLine,
      specialty,
      department,
      departmentCode
    }: CaseSpecialtyUpdate): Promise<void> => {
      const response = await request(
        `/coding-api/processinstance/${amplifyServiceLineAlias(serviceLine)}/${caseRunUid}/v1`,
        {
          cache: 'no-cache',
          redirect: 'follow',
          referrer: 'no-referrer',
          method: 'PUT',
          body: JSON.stringify({
            note_specialty_designation: specialty,
            scheduling_department: department,
            ...(isUsableCell(departmentCode) && {
              scheduling_department_code: departmentCode
            })
          })
        }
      );
      if (!response.ok) {
        throw new Error(`Case specialty update failed with ${response.status}`);
      }

      try {
        const updated = await response.json();
        dispatch(codeBenchActions.fetchActivity(updated) as never);
      } catch (error) {
        console.error('Case specialty saved, but the activity dispatch failed', error);
      }
      try {
        reloadCase();
      } catch (error) {
        console.error('Case specialty saved, but the refresh failed', error);
      }
    },
    loadDictionaryOptions: async ({
      dictionaryName,
      columnName,
      pairedColumnName,
      facility,
      facilityColumn
    }: DictionaryRequest): Promise<readonly DictionaryOption[]> => {
      const searchParams = new URLSearchParams({
        tenantId,
        derivedIndicator: 'true',
        effectiveDate: cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE),
        dictionaryName,
        // Singular, and the endpoint filters on `columnNames` (plural) -- so it binds nothing, no
        // filter is applied, and every column comes back. That accident is the only reason
        // `pairedColumnName` costs no second request. Correcting this name to the plural form
        // means adding `Patient Class Original` to the list in the same edit, or the pairing goes
        // silently missing: no error, no red test, just a class that stops being sent.
        columnName
      });

      const res = await netApi<CMxAPI.RowData>({
        endpoint: `/dictionary/row/unrestricted/v2?${searchParams.toString()}`,
        init: { method: HttpMethods.GET }
      });

      // A null/absent cell would `String()` into a selectable -- and PERSISTABLE -- "null" billing
      // location, and a repeat collides as a React key downstream. The paired cell answers to the
      // same predicate, and must.
      //
      // Keyed on the value so the paired cell survives de-duplication, and the FIRST row carrying
      // a value wins -- as the `[...new Set()]` this replaced did, as the legacy editor's `.find()`
      // does, and as the backend's own derivation (`rows.iterator().next()`) does. A `Map` written
      // with `.set()` on every row would keep the LAST and silently disagree with all three.
      const allRows: readonly Record<string, unknown>[] = res.rowValues ?? [];
      const rows = facilityColumn
        ? allRows.filter((row) => row[facilityColumn] === facility)
        : allRows;
      const options = new Map<string, DictionaryOption>();
      for (const row of rows) {
        const value = row[columnName];
        if (!isUsableCell(value) || options.has(value)) continue;
        const paired = pairedColumnName ? row[pairedColumnName] : undefined;
        options.set(
          value,
          isUsableCell(paired)
            ? { label: value, value, pairedValue: paired }
            : { label: value, value }
        );
      }
      return [...options.values()];
    },
    loadReasonDictionary: async (
      kind: ReasonKind
    ): Promise<readonly ReasonOption[]> => {
      const searchParams = new URLSearchParams({
        tenantId,
        derivedIndicator: 'true',
        effectiveDate: cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE),
        dictionaryName: REASON_DICTIONARY_NAME[kind],
        columnNames: REASON_COLUMNS
      });
      const res = await netApi<CMxAPI.RowData>({
        endpoint: `/dictionary/row/unrestricted/v2?${searchParams.toString()}`,
        init: { method: HttpMethods.GET }
      });
      return parseReasonRows(res.rowValues ?? []);
    },
    loadActionPermissions: async (
      roles: readonly string[]
    ): Promise<ActionPermissions> => {
      const read = async (
        configurationId: string
      ): Promise<Record<string, unknown>> => {
        try {
          const res = await netApi<unknown>({
            endpoint: `/configuration/detail/${configurationId}/${tenantId}/v2?merge=true&serviceLine=${encodeURIComponent(caseServiceLine)}`,
            init: { method: HttpMethods.GET }
          });
          return mergedJsonValue(res);
        } catch {
          return {};
        }
      };
      const [exclude, transfer] = await Promise.all([
        read('exclude-case-permissions'),
        read('case-transfer-permissions')
      ]);
      return {
        canExclude: canExcludeFromConfig(roles, exclude),
        transfer: transferPermissionFromConfig(roles, transfer)
      };
    },
    loadNextCase: async (): Promise<void> => {},
    loadCase: async (): Promise<void> => {},
    startReview: (): void => {},
    onCodeSearch: async (): Promise<void> => {},
    fetchProviderDetails: async ({
      providerNumber,
      providerName,
      tenantCode
    }) => {
      if (!providerNumber) return undefined; // mirrors navigateProvider*'s own guard
      const match = await fetchProviderViaDataAccess(
        dataAccessContext,
        providerName,
        tenantCode
      );
      const found = match?.find((p) => p?.provider_id === providerNumber);
      return found ? toProviderDirectoryEntry(found) : undefined;
    },
    searchTransferRecipients: async (
      term: string
    ): Promise<readonly TransferRecipient[]> => {
      try {
        const { getServiceDeskClient } = await import(
          '../../clients/service-desk.client'
        );
        const client = getServiceDeskClient();
        await client.refreshToken();
        const response = await client.apolloClient.query({
          query: TRANSFER_RECIPIENTS,
          fetchPolicy: 'network-only'
        });
        return narrowByTerm(
          toTransferRecipients(response?.data?.TransferRecipients?.data),
          term
        );
      } catch {
        return [];
      }
    },
    listTransferQueues: async () => {
      let client: ServiceDeskClient;
      try {
        const { getServiceDeskClient } = await import(
          '../../clients/service-desk.client'
        );
        client = getServiceDeskClient();
        await client.refreshToken();
      } catch {
        throw new Error("Couldn't load queues.");
      }
      let response: Awaited<ReturnType<typeof client.apolloClient.query>>;
      try {
        response = await client.apolloClient.query({
          query: TRANSFER_QUEUES,
          variables: {
            service_line: appServiceLines.isIPC(caseServiceLine)
              ? commonEnums.ServiceLines.IPC
              : caseServiceLine,
            exclude_collection_id: currentProfile || null
          },
          fetchPolicy: 'network-only'
        });
      } catch (error) {
        const typed = (
          error as {
            graphQLErrors?: ReadonlyArray<{
              message?: string;
              extensions?: { code?: string };
            }>;
          }
        )?.graphQLErrors?.[0];
        const code = typed?.extensions?.code;
        throw new Error(
          typeof code === 'string' &&
          code.startsWith('TRANSFER_') &&
          typed?.message
            ? typed.message
            : "Couldn't load queues."
        );
      }
      // The service is asked to exclude the current queue; filter here too so a
      // queue the BFF would reject as TRANSFER_NOT_SENT is never offered.
      const offered = (response?.data?.TransferQueues ?? []).filter(
        (queue: { collection_id: string }) =>
          !currentProfile || queue.collection_id !== currentProfile
      );
      return offered.map(
        (queue: {
          collection_id: string;
          name: string;
          description?: string | null;
          case_count?: number | null;
        }) => ({
          kind: 'queue' as const,
          id: queue.collection_id,
          name: queue.name,
          ...(queue.description ? { description: queue.description } : {}),
          ...(typeof queue.case_count === 'number'
            ? { caseCount: queue.case_count }
            : {})
        })
      );
    },
    navigateToWorklist: () =>
      dispatch(
        navigationIntentActions.navigationIntent({
          intent: 'CASE_CHOOSER',
          url: '/cmx/worklist/V2'
        })
      ),
    transferCase: async (
      payload: TransferPayload
    ): Promise<{ destinationName: string }> => {
      let client: ServiceDeskClient;
      try {
        const { getServiceDeskClient } = await import(
          '../../clients/service-desk.client'
        );
        client = getServiceDeskClient();
        await client.refreshToken();
      } catch {
        throw new TransferNotSentError('The transfer was not sent. Try again.');
      }
      try {
        const response = await client.apolloClient.mutate({
          mutation: TRANSFER_CASE,
          variables: {
            input: toTransferCaseInput(payload, processInstance.guid.uuid)
          },
          fetchPolicy: 'no-cache'
        });
        dispatch(codeBenchActions.fetchActivity(processInstance));
        return {
          destinationName:
            response?.data?.TransferCase?.destination_name ??
            payload.destination.name
        };
      } catch (error) {
        throw toTransferError(error);
      }
    }
  };
}
